import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    IconButton,
    useTheme,
    CircularProgress,
    Paper,
    Tooltip,
    LinearProgress,
    Chip,
    Collapse,
    Select,
    MenuItem,
    FormControl,
} from '@mui/material';
import {
    Send,
    SmartToy,
    StopCircle,
    LibraryBooks,
    UploadFile,
    Delete,
    Close,
    CreateNewFolder,
    InsertDriveFile,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';

import remarkGfm from 'remark-gfm';
import {
    streamChatCompletion,
    ChatMessage,
    getDefaultLibrary,
    getLibraries,
    createLibrary,
    getDocuments,
    uploadDocument,
    deleteDocument,
    MistralLibrary,
    MistralDocument,
} from '../services/aiChatService';
import { useAuth } from '../contexts/AuthContext';
import UserAvatar from '../components/UserAvatar';

/* ──────────────────────── Types ──────────────────────── */

interface DisplayMessage {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    timestamp: Date;
    streaming?: boolean;
}

/* ──────────────────────── Helpers ──────────────────────── */

const formatTime = (d: Date) =>
    d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });

const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

/* ──────────────────────── Component ──────────────────────── */

const AIChatPage: React.FC = () => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const { user } = useAuth();

    /* ── Nautilus Console tokens ── */
    const N = {
        deepOcean: '#0f2134',
        sand: '#ccba9b',
        abyssal: '#0a1622',
        bg: isDark ? '#0a1622' : '#f5f5f5',
        surface: isDark ? '#0f2134' : '#ffffff',
        surfaceAlt: isDark ? '#132a3e' : '#fafafa',
        text1: isDark ? '#ffffff' : '#0f2134',
        text2: isDark ? '#b0bec5' : '#546e7a',
        text3: isDark ? '#78909c' : '#90a4ae',
        border: isDark ? 'rgba(204, 186, 155, 0.15)' : 'rgba(15, 33, 52, 0.12)',
        borderStrong: isDark ? 'rgba(204, 186, 155, 0.30)' : 'rgba(15, 33, 52, 0.20)',
        userBubble: isDark ? 'rgba(204, 186, 155, 0.08)' : 'rgba(15, 33, 52, 0.04)',
        aiBubble: isDark ? 'rgba(255,255,255, 0.03)' : 'rgba(0,0,0,0.02)',
        inputBg: isDark ? '#0d1e2e' : '#f0f0f0',
        codeBg: isDark ? '#0a1622' : '#f0f0f0',
        inlineCodeBg: isDark ? 'rgba(204,186,155,0.1)' : 'rgba(15,33,52,0.07)',
    };

    /* ── State ── */
    const [messages, setMessages] = useState<DisplayMessage[]>([]);
    const [input, setInput] = useState('');
    const [isStreaming, setIsStreaming] = useState(false);

    const abortRef = useRef<AbortController | null>(null);
    const bottomRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);

    /* ── RAG / Knowledge Base State ── */
    const [kbOpen, setKbOpen] = useState(false);
    const [libraries, setLibraries] = useState<MistralLibrary[]>([]);
    const [activeLibrary, setActiveLibrary] = useState<MistralLibrary | null>(null);
    const [documents, setDocuments] = useState<MistralDocument[]>([]);
    const [uploading, setUploading] = useState(false);
    const [kbLoading, setKbLoading] = useState(false);
    const [kbError, setKbError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [dragOver, setDragOver] = useState(false);

    /* ── Model Selection State ── */
    const MISTRAL_MODELS = [
        { id: 'mistral-large-latest', name: 'Mistral Large' },
        { id: 'mistral-medium-latest', name: 'Mistral Medium' },
        { id: 'mistral-small-latest', name: 'Mistral Small' },
        { id: 'open-mistral-7b', name: 'Mistral 7B' },
        { id: 'open-mixtral-8x7b', name: 'Mixtral 8x7B' },
    ];
    const [selectedModel, setSelectedModel] = useState('mistral-medium-latest');

    /* ── Auto-scroll ── */
    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    /* ── Focus input on mount ── */
    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    /* ── Load libraries on panel open ── */
    useEffect(() => {
        if (kbOpen && libraries.length === 0) {
            loadLibraries();
        }
    }, [kbOpen]);

    /* ── Load documents when active library changes ── */
    useEffect(() => {
        if (activeLibrary) {
            loadDocuments(activeLibrary.id);
        }
    }, [activeLibrary]);

    const loadLibraries = async () => {
        setKbLoading(true);
        setKbError(null);
        try {
            // Auto-load the pre-configured default library
            const defaultLibId = await getDefaultLibrary();
            if (defaultLibId) {
                const defaultLib: MistralLibrary = {
                    id: defaultLibId,
                    name: 'AI Assistant Knowledge Base',
                };
                setLibraries([defaultLib]);
                setActiveLibrary(defaultLib);
                return;
            }
            // Fallback: list all libraries
            const libs = await getLibraries();
            setLibraries(libs);
            if (libs.length > 0 && !activeLibrary) {
                setActiveLibrary(libs[0]);
            }
        } catch (err: any) {
            setKbError(err.message);
        } finally {
            setKbLoading(false);
        }
    };

    const loadDocuments = async (libId: string) => {
        setKbLoading(true);
        setKbError(null);
        try {
            const docs = await getDocuments(libId);
            setDocuments(docs);
        } catch (err: any) {
            setKbError(err.message);
        } finally {
            setKbLoading(false);
        }
    };

    const handleCreateLibrary = async () => {
        setKbError(null);
        try {
            const lib = await createLibrary('AI Assistant Knowledge Base', 'Documents for RAG context');
            setLibraries(prev => [...prev, lib]);
            setActiveLibrary(lib);
        } catch (err: any) {
            setKbError(err.message);
        }
    };

    const handleUploadFile = async (file: File) => {
        if (!activeLibrary) return;
        setUploading(true);
        setKbError(null);
        try {
            await uploadDocument(activeLibrary.id, file);
            await loadDocuments(activeLibrary.id);
        } catch (err: any) {
            setKbError(err.message);
        } finally {
            setUploading(false);
        }
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) handleUploadFile(file);
        e.target.value = ''; // reset so same file can be re-selected
    };

    const handleDeleteDoc = async (docId: string) => {
        if (!activeLibrary) return;
        setKbError(null);
        try {
            await deleteDocument(activeLibrary.id, docId);
            setDocuments(prev => prev.filter(d => d.id !== docId));
        } catch (err: any) {
            setKbError(err.message);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files?.[0];
        if (file) handleUploadFile(file);
    };

    /* ── Send handler ── */
    const handleSend = useCallback(async () => {
        const trimmed = input.trim();
        if (!trimmed || isStreaming) return;

        const userMsg: DisplayMessage = {
            id: uid(),
            role: 'user',
            content: trimmed,
            timestamp: new Date(),
        };

        const assistantMsg: DisplayMessage = {
            id: uid(),
            role: 'assistant',
            content: '',
            timestamp: new Date(),
            streaming: true,
        };

        setMessages(prev => [...prev, userMsg, assistantMsg]);
        setInput('');
        setIsStreaming(true);

        // Build messages payload for the API (full history)
        const apiMessages: ChatMessage[] = [
            ...messages.map(m => ({ role: m.role, content: m.content })),
            { role: 'user' as const, content: trimmed },
        ];

        const controller = new AbortController();
        abortRef.current = controller;

        try {
            const stream = streamChatCompletion(apiMessages, selectedModel, controller.signal);

            for await (const chunk of stream) {
                setMessages(prev => {
                    const updated = [...prev];
                    const last = updated[updated.length - 1];
                    if (last && last.id === assistantMsg.id) {
                        updated[updated.length - 1] = { ...last, content: last.content + chunk };
                    }
                    return updated;
                });
            }
        } catch (err: any) {
            if (err.name === 'AbortError') {
                // user cancelled — that's fine
            } else {
                setMessages(prev => {
                    const updated = [...prev];
                    const last = updated[updated.length - 1];
                    if (last && last.id === assistantMsg.id) {
                        updated[updated.length - 1] = {
                            ...last,
                            content: last.content || `⚠ Error: ${err.message}`,
                        };
                    }
                    return updated;
                });
            }
        } finally {
            setMessages(prev => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last && last.id === assistantMsg.id) {
                    updated[updated.length - 1] = { ...last, streaming: false };
                }
                return updated;
            });
            setIsStreaming(false);
            abortRef.current = null;
            inputRef.current?.focus();
        }
    }, [input, isStreaming, messages]);

    const handleStop = () => {
        abortRef.current?.abort();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
            return;
        }
    };

    /* ── Markdown styles (applied to the bubble container) ── */
    const markdownSx = {
        '& h1, & h2, & h3, & h4, & h5, & h6': {
            color: N.text1,
            fontWeight: 600,
            mt: 1.5,
            mb: 0.75,
            lineHeight: 1.3,
            letterSpacing: '-0.01em',
        },
        '& h1': { fontSize: '1.25rem' },
        '& h2': { fontSize: '1.1rem' },
        '& h3': { fontSize: '1rem' },
        '& h4': { fontSize: '0.9rem' },
        '& p': {
            my: 0.5,
            lineHeight: 1.6,
            fontSize: '0.875rem',
        },
        '& ul, & ol': {
            pl: 2.5,
            my: 0.5,
            '& li': {
                fontSize: '0.875rem',
                lineHeight: 1.6,
                mb: 0.25,
                '&::marker': { color: N.text3 },
            },
        },
        '& pre': {
            bgcolor: N.codeBg,
            border: `1px solid ${N.border}`,
            borderRadius: 1,
            p: 1.5,
            my: 1,
            overflowX: 'auto',
            '& code': {
                fontFamily: '"Roboto Mono", monospace',
                fontSize: '0.8rem',
                lineHeight: 1.6,
                bgcolor: 'transparent',
                p: 0,
                borderRadius: 0,
            },
        },
        '& :not(pre) > code': {
            bgcolor: N.inlineCodeBg,
            px: '5px',
            py: '1px',
            borderRadius: 0.5,
            fontFamily: '"Roboto Mono", monospace',
            fontSize: '0.82em',
        },
        '& blockquote': {
            borderLeft: `3px solid ${N.sand}`,
            pl: 1.5,
            ml: 0,
            my: 1,
            color: N.text2,
            fontStyle: 'italic',
        },
        '& table': {
            borderCollapse: 'collapse',
            width: '100%',
            my: 1,
            '& th, & td': {
                border: `1px solid ${N.border}`,
                px: 1,
                py: 0.5,
                fontSize: '0.82rem',
                textAlign: 'left',
            },
            '& th': {
                bgcolor: N.codeBg,
                fontWeight: 600,
            },
        },
        '& hr': {
            border: 'none',
            borderTop: `1px solid ${N.border}`,
            my: 1.5,
        },
        '& a': {
            color: N.sand,
            textDecoration: 'underline',
            textDecorationColor: 'rgba(204,186,155,0.4)',
            '&:hover': { textDecorationColor: N.sand },
        },
        '& strong': { fontWeight: 600 },
        '& em': { fontStyle: 'italic' },
    };

    /* ────────────────────── Render ────────────────────── */
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: 'calc(100vh - 100px)',
                maxWidth: 900,
                mx: 'auto',
                gap: 0,
            }}
        >
            {/* ── Header ── */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    py: 1.5,
                    px: 2,
                    borderBottom: `1px solid ${N.border}`,
                    flexShrink: 0,
                }}
            >
                <SmartToy sx={{ color: N.sand, fontSize: 28 }} />
                <Box>
                    <Typography
                        variant="overline"
                        sx={{
                            color: N.text1,
                            fontWeight: 700,
                            letterSpacing: '0.05em',
                            lineHeight: 1.2,
                            fontSize: '0.85rem',
                        }}
                    >
                        AI ASSISTANT
                    </Typography>
                    <Typography
                        variant="caption"
                        sx={{
                            display: 'block',
                            color: N.text3,
                            fontFamily: 'monospace',
                            fontSize: '0.7rem',
                            letterSpacing: '0.02em',
                        }}
                    >
                        AGENT ONLINE • MISTRAL
                    </Typography>
                </Box>

                {/* Crafted UI: The Abyssal Switch (Polished Pill) */}
                <Box
                    sx={{
                        ml: 'auto',
                        mr: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '2px',
                        bgcolor: isDark ? 'rgba(0,0,0,0.15)' : 'rgba(0,0,0,0.03)',
                        p: '4px',
                        borderRadius: '8px',
                        border: `1px solid ${N.border}`,
                    }}
                >
                    {MISTRAL_MODELS.map((m) => {
                        const isActive = selectedModel === m.id;
                        return (
                            <Box
                                key={m.id}
                                onClick={() => setSelectedModel(m.id)}
                                sx={{
                                    px: 1.75,
                                    py: 0.6,
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    bgcolor: isActive ? N.surfaceAlt : 'transparent',
                                    border: `1px solid ${isActive ? N.borderStrong : 'transparent'}`,
                                    boxShadow: isActive 
                                        ? '0 2px 6px rgba(0,0,0,0.15), 0 0 0 1px rgba(255,255,255,0.02) inset' 
                                        : 'none',
                                    '&:hover': {
                                        bgcolor: isActive ? N.surfaceAlt : 'rgba(204, 186, 155, 0.05)',
                                    },
                                }}
                            >
                                {/* Active Indicator Dot */}
                                <Box
                                    sx={{
                                        width: 6,
                                        height: 6,
                                        borderRadius: '50%',
                                        bgcolor: isActive ? '#4ae38c' : N.text3,
                                        opacity: isActive ? 1 : 0.3,
                                        boxShadow: isActive ? '0 0 8px rgba(74, 227, 140, 0.4)' : 'none',
                                        transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                                    }}
                                />
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontSize: '0.7rem',
                                        fontFamily: '"Inter", "Roboto", sans-serif',
                                        fontWeight: isActive ? 600 : 500,
                                        color: isActive ? N.text1 : N.text3,
                                        whiteSpace: 'nowrap',
                                        transition: 'color 0.2s ease',
                                    }}
                                >
                                    {m.name.replace('Mistral ', '').replace('Mixtral ', '')}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>

                {isStreaming && (
                    <CircularProgress
                        size={16}
                        thickness={5}
                        sx={{ color: N.sand, ml: 'auto' }}
                    />
                )}
                {!isStreaming && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Knowledge Base">
                            <IconButton
                                onClick={() => setKbOpen(prev => !prev)}
                                size="small"
                                sx={{
                                    color: kbOpen ? N.sand : N.text3,
                                    border: `1px solid ${kbOpen ? N.sand : N.border}`,
                                    borderRadius: 1,
                                    transition: 'all 0.2s',
                                    '&:hover': { borderColor: N.sand, color: N.sand },
                                }}
                            >
                                <LibraryBooks fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
            </Box>

            {/* ── Knowledge Base Panel ── */}
            <Collapse in={kbOpen}>
                <Box
                    sx={{
                        px: 2,
                        py: 1.5,
                        borderBottom: `1px solid ${N.border}`,
                        bgcolor: N.surfaceAlt,
                    }}
                >
                    {/* Hidden file input */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        hidden
                        accept=".pdf,.txt,.md,.docx,.csv,.json,.html"
                        onChange={handleFileSelect}
                    />

                    {kbLoading && <LinearProgress sx={{ mb: 1, '& .MuiLinearProgress-bar': { bgcolor: N.sand } }} />}

                    {kbError && (
                        <Typography variant="caption" sx={{ color: '#e57373', display: 'block', mb: 1 }}>
                            ⚠ {kbError}
                        </Typography>
                    )}

                    {/* No library yet */}
                    {!kbLoading && libraries.length === 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" sx={{ color: N.text3 }}>
                                No knowledge base yet.
                            </Typography>
                            <Chip
                                icon={<CreateNewFolder sx={{ fontSize: 16 }} />}
                                label="Create Library"
                                size="small"
                                onClick={handleCreateLibrary}
                                sx={{
                                    borderColor: N.sand,
                                    color: N.sand,
                                    '& .MuiChip-icon': { color: N.sand },
                                    '&:hover': { bgcolor: 'rgba(204,186,155,0.12)' },
                                }}
                                variant="outlined"
                            />
                        </Box>
                    )}

                    {/* Library active — show docs + upload */}
                    {activeLibrary && (
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: N.text2,
                                        fontFamily: 'monospace',
                                        fontSize: '0.7rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                    }}
                                >
                                    KNOWLEDGE BASE • {documents.length} doc{documents.length !== 1 ? 's' : ''}
                                </Typography>
                                <Box sx={{ flex: 1 }} />
                                <Tooltip title="Upload file">
                                    <IconButton
                                        size="small"
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={uploading}
                                        sx={{
                                            color: N.sand,
                                            border: `1px solid ${N.border}`,
                                            borderRadius: 1,
                                            '&:hover': { borderColor: N.sand },
                                        }}
                                    >
                                        <UploadFile sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Close">
                                    <IconButton
                                        size="small"
                                        onClick={() => setKbOpen(false)}
                                        sx={{ color: N.text3 }}
                                    >
                                        <Close sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>

                            {uploading && (
                                <LinearProgress sx={{ mb: 1, borderRadius: 1, '& .MuiLinearProgress-bar': { bgcolor: N.sand } }} />
                            )}

                            {/* Drop zone + document list */}
                            <Box
                                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                                onDragLeave={() => setDragOver(false)}
                                onDrop={handleDrop}
                                sx={{
                                    border: `1px dashed ${dragOver ? N.sand : N.border}`,
                                    borderRadius: 1,
                                    p: 1,
                                    minHeight: 48,
                                    transition: 'border-color 0.2s',
                                    bgcolor: dragOver ? 'rgba(204,186,155,0.05)' : 'transparent',
                                }}
                            >
                                {documents.length === 0 && !uploading && (
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: N.text3,
                                            display: 'block',
                                            textAlign: 'center',
                                            py: 1,
                                            fontFamily: 'monospace',
                                            fontSize: '0.7rem',
                                        }}
                                    >
                                        Drop files here or click upload
                                    </Typography>
                                )}

                                {documents.map(doc => (
                                    <Box
                                        key={doc.id}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            py: 0.5,
                                            px: 0.5,
                                            borderRadius: 0.5,
                                            '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' },
                                        }}
                                    >
                                        <InsertDriveFile sx={{ fontSize: 16, color: N.text3 }} />
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                flex: 1,
                                                color: N.text2,
                                                fontSize: '0.78rem',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {doc.name}
                                        </Typography>
                                        <Tooltip title="Remove">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDeleteDoc(doc.id)}
                                                sx={{
                                                    color: N.text3,
                                                    p: 0.25,
                                                    '&:hover': { color: '#e57373' },
                                                }}
                                            >
                                                <Delete sx={{ fontSize: 14 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )}
                </Box>
            </Collapse>

            {/* ── Message List ── */}
            <Box
                sx={{
                    flex: 1,
                    overflowY: 'auto',
                    px: 2,
                    py: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    '&::-webkit-scrollbar': { width: 6 },
                    '&::-webkit-scrollbar-track': { background: 'transparent' },
                    '&::-webkit-scrollbar-thumb': {
                        background: N.border,
                        borderRadius: 3,
                    },
                }}
            >
                {messages.length === 0 && (
                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 2,
                            opacity: 0.5,
                        }}
                    >
                        <SmartToy sx={{ fontSize: 56, color: N.text3 }} />
                        <Typography variant="body2" sx={{ color: N.text3, textAlign: 'center' }}>
                            Start a conversation with the AI Assistant.
                            <br />
                            <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                Shift+Enter for new line
                            </span>
                        </Typography>
                    </Box>
                )}

                {messages.map(msg => (
                    <Box
                        key={msg.id}
                        sx={{
                            display: 'flex',
                            flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
                            gap: 1,
                            alignItems: 'flex-start',
                        }}
                    >
                        {/* Avatar */}
                        {msg.role === 'user' ? (
                            <UserAvatar
                                user={user || undefined}
                                sx={{
                                    width: 30,
                                    height: 30,
                                    fontSize: '0.75rem',
                                    borderRadius: 1,
                                    border: `1px solid ${N.border}`,
                                    flexShrink: 0,
                                    mt: 0.25,
                                }}
                            />
                        ) : (
                            <Box
                                sx={{
                                    width: 30,
                                    height: 30,
                                    borderRadius: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                    border: `1px solid ${N.border}`,
                                    bgcolor: N.aiBubble,
                                    mt: 0.25,
                                }}
                            >
                                <SmartToy sx={{ fontSize: 18, color: N.sand }} />
                            </Box>
                        )}

                        {/* Bubble */}
                        <Paper
                            elevation={0}
                            sx={{
                                maxWidth: '78%',
                                px: 2,
                                py: 1.25,
                                borderRadius: 1.5,
                                bgcolor: msg.role === 'user' ? N.userBubble : N.aiBubble,
                                border: `1px solid ${msg.role === 'user' ? N.borderStrong : N.border}`,
                                position: 'relative',
                                color: N.text1,
                                wordBreak: 'break-word',
                                ...markdownSx,
                            }}
                        >
                            {msg.role === 'assistant' ? (
                                msg.content ? (
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                        {msg.content}
                                    </ReactMarkdown>
                                ) : (
                                    msg.streaming && (
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: N.text3,
                                                fontFamily: 'monospace',
                                                animation: 'pulse 1.5s ease-in-out infinite',
                                                '@keyframes pulse': {
                                                    '0%, 100%': { opacity: 0.4 },
                                                    '50%': { opacity: 1 },
                                                },
                                            }}
                                        >
                                            ···
                                        </Typography>
                                    )
                                )
                            ) : (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        lineHeight: 1.6,
                                        fontSize: '0.875rem',
                                        whiteSpace: 'pre-wrap',
                                    }}
                                >
                                    {msg.content}
                                </Typography>
                            )}

                            {/* Timestamp */}
                            <Typography
                                variant="caption"
                                sx={{
                                    display: 'block',
                                    mt: 0.5,
                                    color: N.text3,
                                    fontFamily: 'monospace',
                                    fontSize: '0.65rem',
                                    textAlign: msg.role === 'user' ? 'right' : 'left',
                                    letterSpacing: '0.03em',
                                }}
                            >
                                {formatTime(msg.timestamp)}
                            </Typography>
                        </Paper>
                    </Box>
                ))}

                <div ref={bottomRef} />
            </Box>

            {/* ── Input Bar ── */}
            <Box
                sx={{
                    px: 2,
                    py: 1.5,
                    borderTop: `1px solid ${N.border}`,
                    flexShrink: 0,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-end',
                        gap: 1,
                        bgcolor: N.inputBg,
                        border: `1px solid ${N.border}`,
                        borderRadius: 1.5,
                        px: 1.5,
                        py: 0.5,
                        transition: 'border-color 0.2s',
                        position: 'relative',
                        '&:focus-within': {
                            borderColor: N.sand,
                        },
                    }}
                >
                    <Box
                        component="textarea"
                        ref={inputRef}
                        value={input}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Type a message…"
                        rows={1}
                        disabled={isStreaming}
                        sx={{
                            flex: 1,
                            border: 'none',
                            outline: 'none',
                            resize: 'none',
                            bgcolor: 'transparent',
                            color: N.text1,
                            fontFamily: 'Inter, Roboto, sans-serif',
                            fontSize: '0.9rem',
                            lineHeight: 1.5,
                            py: 1,
                            minHeight: 24,
                            maxHeight: 140,
                            '&::placeholder': { color: N.text3 },
                            '&:disabled': { opacity: 0.5 },
                        }}
                    />
                    {isStreaming ? (
                        <IconButton
                            onClick={handleStop}
                            size="small"
                            sx={{
                                color: '#e57373',
                                mb: 0.25,
                                '&:hover': { bgcolor: 'rgba(229,115,115,0.1)' },
                            }}
                            aria-label="Stop generating"
                        >
                            <StopCircle fontSize="small" />
                        </IconButton>
                    ) : (
                        <IconButton
                            onClick={handleSend}
                            disabled={!input.trim()}
                            size="small"
                            sx={{
                                color: input.trim() ? N.sand : N.text3,
                                mb: 0.25,
                                transition: 'color 0.2s',
                                '&:hover': { bgcolor: 'rgba(204,186,155,0.12)' },
                            }}
                            aria-label="Send message"
                        >
                            <Send fontSize="small" />
                        </IconButton>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default AIChatPage;
